import { Checkbox } from '@thanx/uikit/checkbox'
import { Text } from '@thanx/uikit/text'
import { locationsApi } from 'api/locations'
import ModalContainer from 'components/ModalContainer'
import Spinner from 'components/Spinner'
import { buildTranslate } from 'locales'
import { useEffect, useMemo, useState } from 'react'

const t = buildTranslate('merchant_users.rbac')

const LOCATION_LIST_MAX_HEIGHT = 400

type Props = {
  isOpen: boolean
  initialLocationIds: number[]
  onSave: (locationIds: number[]) => void
  onClose: () => void
  disabled?: boolean
}

export const RbacLocationPickerModal = ({
  isOpen,
  initialLocationIds,
  onSave,
  onClose,
  disabled = false,
}: Props) => {
  const { data, isLoading } = locationsApi.useGetLocationsQuery({
    full_visibility: true,
  })
  const [selected, setSelected] = useState<number[]>(initialLocationIds)

  useEffect(() => {
    if (isOpen) {
      setSelected(initialLocationIds)
    }
  }, [isOpen, initialLocationIds])

  const locations = data?.locations ?? []
  const selectedSet = useMemo(() => new Set(selected), [selected])

  const handleToggle = (id: number, checked: boolean) => {
    setSelected(prev => (checked ? [...prev, id] : prev.filter(x => x !== id)))
  }

  const handleSelectAll = () => setSelected(locations.map(l => l.id))
  const handleDeselectAll = () => setSelected([])

  const handleProceed = () => {
    onSave(selected)
    onClose()
  }

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={onClose}
      title={t('locations_modal_title')}
      proceedText={t('locations_modal_proceed')}
      onProceed={handleProceed}
      showBottomProceed={!disabled}
    >
      <Spinner isLoading={isLoading}>
        {!disabled && (
          <div className="my-m">
            <button
              type="button"
              className="border-0 bg-transparent p-0 cursor-pointer mr-m"
              onClick={handleSelectAll}
            >
              <Text.BodyText3 tag="span" color="primary">
                {t('select_all')}
              </Text.BodyText3>
            </button>
            <button
              type="button"
              className="border-0 bg-transparent p-0 cursor-pointer"
              onClick={handleDeselectAll}
            >
              <Text.BodyText3 tag="span" color="primary">
                {t('deselect_all')}
              </Text.BodyText3>
            </button>
          </div>
        )}
        <div
          className="overflow-auto"
          style={{ maxHeight: LOCATION_LIST_MAX_HEIGHT }}
        >
          {locations.map(location => (
            <div key={location.id} className="d-flex align-items-start mb-s">
              <Checkbox
                checked={selectedSet.has(location.id)}
                onChange={checked => handleToggle(location.id, checked)}
                disabled={disabled}
              />
              <div className="ml-xs">
                <Text.Header4 className="mb-0">
                  {location.name || location.street}
                </Text.Header4>
                <Text.BodyText4 color="grey70">
                  {`${location.street}, ${location.city}, ${location.zip}, ${location.state}`}
                </Text.BodyText4>
              </div>
            </div>
          ))}
        </div>
      </Spinner>
    </ModalContainer>
  )
}
